<script setup>
// NSSoft (03/11/2026): cabecalho das colunas da lista no modo expandido. Clicar numa coluna ordena a lista
// (no servidor, valendo para todas as paginas e para as pastas); clicar de novo inverte. O campo de busca
// filtra a mesma lista sem abrir outra tela. As larguras seguem ConversationCardExpanded.vue.
import { computed } from 'vue';
import { useBreakpoints } from '@vueuse/core';
import { useMapGetter } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  sortBy: { type: String, default: '' },
  busca: { type: String, default: '' },
});

const emit = defineEmits(['sort', 'update:busca']);

const breakpoints = useBreakpoints({
  lg: wootConstants.LARGE_SCREEN_BREAKPOINT,
});
const isLgScreen = breakpoints.greaterOrEqual('lg');

const activeInbox = useMapGetter('getSelectedInbox');
const inboxesList = useMapGetter('inboxes/getInboxes');
const showInboxName = computed(
  () => !activeInbox.value && inboxesList.value.length > 1
);

// coluna -> [base da ordenacao, sentido do primeiro clique]
const COLUNAS = {
  prioridade: ['priority', 'desc'],
  responsavel: ['assignee_name', 'asc'],
  situacao: ['status', 'asc'],
  caixa: ['inbox', 'asc'],
  numero: ['display_id', 'desc'],
  contato: ['contact_name', 'asc'],
  etiquetas: ['labels', 'asc'],
  atividade: ['last_activity_at', 'desc'],
};

const sentidoDe = coluna => {
  const [base] = COLUNAS[coluna];
  if (props.sortBy === `${base}_asc`) return 'asc';
  if (props.sortBy === `${base}_desc`) return 'desc';
  return '';
};

const seta = coluna => {
  const s = sentidoDe(coluna);
  if (s === 'asc') return '▲';
  if (s === 'desc') return '▼';
  return '';
};

const ordenar = coluna => {
  const [base, primeiro] = COLUNAS[coluna];
  const atual = sentidoDe(coluna);
  let sentido = primeiro;
  if (atual) sentido = atual === 'asc' ? 'desc' : 'asc';
  emit('sort', `${base}_${sentido}`);
};

// textos da versao NSSoft (pt-BR); ficam aqui para o template nao ter texto solto
const T = {
  caixa: 'Caixa',
  numero: 'Nº',
  contato: 'Contato',
  etiquetas: 'Etiquetas',
  atividade: 'Tempo',
  busca: 'Buscar: nº, contato, e-mail, telefone, assunto, etiqueta, texto',
};

const valorBusca = computed({
  get: () => props.busca,
  set: v => emit('update:busca', v),
});
</script>

<template>
  <div
    v-if="isLgScreen"
    class="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4 items-center px-3 h-9 border-b border-n-slate-3 bg-n-surface-1 text-xs font-medium text-n-slate-11 select-none flex-shrink-0"
  >
    <div class="flex items-center gap-2 min-w-0 flex-1">
      <div class="w-4 flex-shrink-0" />
      <div class="w-px h-3 flex-shrink-0" />
      <button
        v-tooltip.top="'Ordenar por prioridade'"
        class="w-4 flex-shrink-0 text-center hover:text-n-slate-12"
        :class="{ 'text-n-brand': seta('prioridade') }"
        @click="ordenar('prioridade')"
      >
        {{ seta('prioridade') || 'P' }}
      </button>
      <button
        v-tooltip.top="'Ordenar por responsável'"
        class="w-4 flex-shrink-0 text-center hover:text-n-slate-12"
        :class="{ 'text-n-brand': seta('responsavel') }"
        @click="ordenar('responsavel')"
      >
        {{ seta('responsavel') || 'R' }}
      </button>
      <button
        v-tooltip.top="'Ordenar por situação'"
        class="w-4 flex-shrink-0 text-center hover:text-n-slate-12"
        :class="{ 'text-n-brand': seta('situacao') }"
        @click="ordenar('situacao')"
      >
        {{ seta('situacao') || 'S' }}
      </button>
      <div class="w-px h-3 flex-shrink-0" />
      <template v-if="showInboxName">
        <button
          class="w-20 flex-shrink-0 text-start truncate hover:text-n-slate-12"
          :class="{ 'text-n-brand': seta('caixa') }"
          @click="ordenar('caixa')"
        >
          {{ T.caixa }} {{ seta('caixa') }}
        </button>
        <div class="w-px h-3 flex-shrink-0" />
      </template>
      <button
        class="max-w-20 w-full flex-shrink-0 text-start truncate hover:text-n-slate-12"
        :class="{ 'text-n-brand': seta('numero') }"
        @click="ordenar('numero')"
      >
        {{ T.numero }} {{ seta('numero') }}
      </button>
      <button
        class="w-[10.5rem] flex-shrink-0 text-start truncate hover:text-n-slate-12"
        :class="{ 'text-n-brand': seta('contato') }"
        @click="ordenar('contato')"
      >
        {{ T.contato }} {{ seta('contato') }}
      </button>
      <input
        v-model="valorBusca"
        type="search"
        :placeholder="T.busca"
        class="flex-1 min-w-0 !mb-0 !h-7 !py-0 !px-2 !text-xs rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10"
        @click.stop
      />
    </div>
    <div class="flex items-center justify-end gap-1.5 flex-shrink-0">
      <button
        class="min-w-0 w-full text-end truncate hover:text-n-slate-12"
        :class="{ 'text-n-brand': seta('etiquetas') }"
        @click="ordenar('etiquetas')"
      >
        {{ T.etiquetas }} {{ seta('etiquetas') }}
      </button>
      <button
        class="flex-shrink-0 w-[4.375rem] text-end truncate hover:text-n-slate-12"
        :class="{ 'text-n-brand': seta('atividade') }"
        @click="ordenar('atividade')"
      >
        {{ T.atividade }} {{ seta('atividade') }}
      </button>
    </div>
  </div>
</template>
